import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { decodeUrlParam } from '../../utils/urlUtils';
import swal from 'sweetalert';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { useSelector, useDispatch } from 'react-redux';
import { setFilterField } from '../../app/itemGridPreferencesSlice';
import GridSectionLoader from '../../components/CustomComponents/preLoad/gridSectionLoader';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import StaffTabBar from './staffTabBar';
import useThemeColor from '../../hooks/useThemeColor';
import useDarkMode from '../../hooks/useDarkMode';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import Select from 'react-select';
import moment from 'moment';
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from './dropdownStyles';
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from '../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar';
import { getModernGridFilterPillSelectStyles } from '../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles';
import { normalizeRosterLeaveList } from '../../utils/rosterLeaveUtils';

const ENTRY_TYPE_OPTIONS = [
  { value: 'All', label: 'All Entries' },
  { value: 'Regular', label: 'Regular Shifts' },
  { value: 'Break', label: 'Breaks' },
  { value: 'Overtime', label: 'Overtime' },
];

const PAID_STATUS_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Unpaid', label: 'Unpaid' },
];

const StaffShiftReport = () => {
  const { UserName: encodedUserName } = useParams();
  const UserName = decodeUrlParam(encodedUserName);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [staffLeaves, setStaffLeaves] = useState([]);
  
  const pageKey = `staffShiftReport_${UserName || 'default'}`;
  
  // Get preferences from Redux
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );

  // Default filter values
  const defaultEntryType = 'All';
  const defaultPaidStatus = 'All';
  const defaultStartDate = moment().subtract(6, 'days').toDate();
  const defaultEndDate = moment().toDate();

  // Get date range from URL query params, Redux preferences, or use defaults
  const getInitialStartDate = () => {
    // Priority: URL params > Redux > Defaults
    const startDateParam = searchParams.get('startDate');
    if (startDateParam && moment(startDateParam, 'YYYY-MM-DD', true).isValid()) {
      return moment(startDateParam, 'YYYY-MM-DD').toDate();
    }
    if (preferences.startDate) {
      const parsed = moment(preferences.startDate);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }
    return defaultStartDate;
  };

  const getInitialEndDate = () => {
    // Priority: URL params > Redux > Defaults
    const endDateParam = searchParams.get('endDate');
    if (endDateParam && moment(endDateParam, 'YYYY-MM-DD', true).isValid()) {
      return moment(endDateParam, 'YYYY-MM-DD').toDate();
    }
    if (preferences.endDate) {
      const parsed = moment(preferences.endDate);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }
    return defaultEndDate;
  };

  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [filters, setFilters] = useState({
    entryType: preferences.entryType || defaultEntryType,
    searchNotes: '', // Don't persist search notes
    paidStatus: preferences.paidStatus || defaultPaidStatus
  });

  const initialFilterSnapshot = useRef(null);
  useEffect(() => {
    initialFilterSnapshot.current = JSON.stringify({
      startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
      endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
      entryType: filters.entryType,
      paidStatus: filters.paidStatus
    });
  }, []);

  const isFilterDirty = useMemo(() => {
    if (!initialFilterSnapshot.current) return false;
    const current = JSON.stringify({
      startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
      endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
      entryType: filters.entryType,
      paidStatus: filters.paidStatus
    });
    return current !== initialFilterSnapshot.current;
  }, [startDate, endDate, filters.entryType, filters.paidStatus]);

  const handleTabChangeBeforeNavigate = useCallback(async (clickedTabId, clickedTabLink) => {
    if (!isFilterDirty) return true;

    const result = await swal({
      title: 'Unsaved filter changes',
      text: 'You have changed filters. Leave anyway?',
      icon: 'warning',
      buttons: {
        cancel: 'Stay',
        discard: 'Leave',
      },
      closeOnClickOutside: false,
    });

    if (result === 'discard') return true;
    return false;
  }, [isFilterDirty]);

  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const [compCurrency, setCompCurrency] = useState("GBP");


  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨", // Handle legacy "Rs" code
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Normalize currency code (handle legacy "Rs" -> "PKR")
  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === "Rs") return "PKR";
    return currencyCode || "GBP";
  };

  useEffect(() => {
    // Fetch company currency from profile data
    if (profileData?.compCurrency) {
      const normalizedCurrency = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalizedCurrency);
    }
  }, [profileData]);

  // Initialize Redux preferences if they don't exist
  useEffect(() => {
    if (!preferences.entryType) {
      dispatch(setFilterField({ pageKey, field: 'entryType', value: defaultEntryType }));
    }
    if (!preferences.paidStatus) {
      dispatch(setFilterField({ pageKey, field: 'paidStatus', value: defaultPaidStatus }));
    }
    if (!preferences.startDate) {
      dispatch(setFilterField({ 
        pageKey, 
        field: 'startDate', 
        value: moment(defaultStartDate).toISOString() 
      }));
    }
    if (!preferences.endDate) {
      dispatch(setFilterField({ 
        pageKey, 
        field: 'endDate', 
        value: moment(defaultEndDate).toISOString() 
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const fetchStaffData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: { 
          UserName,
          CompID 
        }
      });
      const data = response.data?.data || response.data;
      if (data) {
        setStaffData(data);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff data');
      console.error('Error fetching staff data:', err);
    }
  }, [UserName, CompID]);



  const fetchStaffLeaves = useCallback(async () => {
    if (!CompID || !staffData?.userName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_ROSTER_LEAVE_DAYS, {
        params: {
          CompID,
          UserName: staffData.userName,
          StartDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : null,
          EndDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : null,
        },
      });

      const data = response.data?.data || response.data || [];
      const normalizedLeaves = normalizeRosterLeaveList(data);

      setStaffLeaves(normalizedLeaves);
    } catch (error) {
      console.error('Error fetching staff leaves:', error);
      setStaffLeaves([]);
    }
  }, [CompID, staffData?.userName, startDate, endDate]);

  const fetchWorkReport = useCallback(async () => {
    if (!staffData?.userName) return;

    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_WORK_REPORT, {
        params: {
          userName: staffData.userName,
          compID: CompID,
          startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
          endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : ''
        }
      });
 
      const data = response.data?.data || response.data;
      if (data) {
        setReportData(data);
      }
 
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch work report';
      setError(errorMsg);
      showErrorToast(errorMsg);
      console.error('Error fetching work report:', err);
    } finally {
      setLoading(false);
    }
  }, [staffData?.userName, CompID, startDate, endDate]);




  useEffect(() => {
    if (UserName && CompID) {
      fetchStaffData();
    }
  }, [UserName, CompID, fetchStaffData]);



  useEffect(() => {
    if (staffData?.userName) {
      fetchWorkReport();
      fetchStaffLeaves();
    }
  }, [staffData?.userName, startDate, endDate, fetchWorkReport]);



  const toggleDayExpansion = (date) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDays(newExpanded);
  };




  const getEntryIcon = (entry) => {
    if (entry.isBreak) {
      return 'fa fa-coffee';
    } else if (entry.isOvertime) {
      return 'fa fa-arrow-trend-up';
    } else {
      return 'fa fa-clock';
    }
  };



  const getEntryBadgeClass = (entry) => {
    if (entry.isBreak) {
      return entry.breakType === 'UNPAID' ? 'bg-warning' : 'bg-success';
    } else if (entry.isOvertime) {
      return 'bg-purple';
    } else {
      return 'bg-primary';
    }
  };




  const getEntryTypeLabel = (entry) => {
    if (entry.isBreak) {
      return `${entry.breakType} Break`;
    } else if (entry.isOvertime) {
      return 'Overtime';
    } else {
      return 'Regular Shift';
    }
  };




  const getEntryPaidStatus = (entry) => {
    if (!entry) return false;

    const interpretBooleanLike = (value) => {
      if (value === undefined || value === null) return null;

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'number') {
        if (Number.isNaN(value)) return null;
        return value > 0;
      }

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return null;

        if (['true', '1', 'yes', 'y', 'paid', 'complete', 'completed', 'processed'].includes(normalized)) {
          return true;
        }

        if (['false', '0', 'no', 'n', 'unpaid', 'pending', 'notpaid', 'not-paid', 'open', 'outstanding'].includes(normalized)) {
          return false;
        }

        const numericValue = Number(normalized);
        if (!Number.isNaN(numericValue)) {
          return numericValue > 0;
        }

        return null;
      }

      return null;
    };

    const considerValues = (values = []) => {
      let hasFalse = false;
      for (const value of values) {
        const interpreted = interpretBooleanLike(value);
        if (interpreted === true) {
          return { truthy: true, hasFalse };
        }
        if (interpreted === false) {
          hasFalse = true;
        }
      }
      return { truthy: false, hasFalse };
    };

    const paidFlags = considerValues([
      entry.isPaid,
      entry.is_paid,
      entry.isPaidEntry,
      entry.is_Paid,
      entry.IsPaid,
      entry.Is_Paid
    ]);

    if (paidFlags.truthy) return true;

    const statusFlags = considerValues([
      entry.paidStatus,
      entry.paymentStatus,
      entry.paid_status,
      entry.payment_status
    ]);

    if (statusFlags.truthy) return true;

    const payoutRefs = [
      entry.payoutId,
      entry.payoutID,
      entry.payout_id,
      entry.payout_ID,
      entry.PayoutId,
      entry.PayoutID,
      entry.Payout_ID,
      entry.payoutUUID,
      entry.payoutUuid,
      entry.PayoutUUID,
      entry.Payout_Uuid
    ];

    const payoutFlags = considerValues(payoutRefs);
    if (payoutFlags.truthy) return true;

    const paidDateValues = [
      entry.paidDateTime,
      entry.paid_date_time,
      entry.paidDate,
      entry.PaidDate,
      entry.PaidDateTime,
      entry.Paid_DateTime
    ];

    const hasPaidDate = paidDateValues.some((value) => value !== undefined && value !== null && `${value}`.trim() !== '');
    if (hasPaidDate) return true;

    if (paidFlags.hasFalse || statusFlags.hasFalse || payoutFlags.hasFalse) {
      return false;
    }

    return false;
  };




  const formatCurrency = (amount) => {
    try {
      // Use dynamic currency from company settings
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      // Fallback to simple formatting with symbol if currency code is invalid
      console.error('Currency formatting error:', error);
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };




  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };




  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Save to Redux (except searchNotes which we don't persist)
    if (field !== 'searchNotes') {
      dispatch(setFilterField({ pageKey, field, value }));
    }
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;

    // Clear → restore last 7 days instead of leaving nulls
    if (!newStartDate && !newEndDate) {
      const resetStart = moment().subtract(6, 'days').toDate();
      const resetEnd = moment().toDate();
      setStartDate(resetStart);
      setEndDate(resetEnd);
      dispatch(setFilterField({
        pageKey,
        field: 'startDate',
        value: moment(resetStart).toISOString(),
      }));
      dispatch(setFilterField({
        pageKey,
        field: 'endDate',
        value: moment(resetEnd).toISOString(),
      }));
      return;
    }

    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      setStartDate(newStartDate);
      setEndDate(newEndDate || newStartDate);
      if (newStartDate) {
        dispatch(setFilterField({
          pageKey,
          field: 'startDate',
          value: moment(newStartDate).toISOString(),
        }));
      }
      if (newEndDate || newStartDate) {
        dispatch(setFilterField({
          pageKey,
          field: 'endDate',
          value: moment(newEndDate || newStartDate).toISOString(),
        }));
      }
    } else {
      showErrorToast('Invalid date selected');
    }
  };




  const resetFilters = () => {
    const resetStartDate = moment().subtract(6, 'days').toDate();
    const resetEndDate = moment().toDate();
    
    setStartDate(resetStartDate);
    setEndDate(resetEndDate);
    setFilters({
      entryType: defaultEntryType,
      searchNotes: '',
      paidStatus: defaultPaidStatus
    });
    setStaffLeaves([]);
    
    // Reset Redux preferences
    dispatch(setFilterField({ pageKey, field: 'entryType', value: defaultEntryType }));
    dispatch(setFilterField({ pageKey, field: 'paidStatus', value: defaultPaidStatus }));
    dispatch(setFilterField({ 
      pageKey, 
      field: 'startDate', 
      value: moment(resetStartDate).toISOString() 
    }));
    dispatch(setFilterField({ 
      pageKey, 
      field: 'endDate', 
      value: moment(resetEndDate).toISOString() 
    }));
  };

  // Calculate total leave hours
  const toNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : defaultValue;
  };

  const isSalaryStaff = useMemo(() => {
    if (reportData?.weekStats && reportData.weekStats.isSalaryStaff !== undefined) {
      return Boolean(reportData.weekStats.isSalaryStaff);
    }
    if (staffData) {
      if (staffData.IsSalaryStaff !== undefined) return Boolean(staffData.IsSalaryStaff);
      if (staffData.isSalaryStaff !== undefined) return Boolean(staffData.isSalaryStaff);
    }
    return false;
  }, [reportData?.weekStats, staffData]);

  const totalLeaveHours = staffLeaves.reduce((total, leave) => total + (leave.TotalHours || 0), 0);
  const paidLeaveHours = staffLeaves.filter(l => l.IsPaid).reduce((total, leave) => total + (leave.TotalHours || 0), 0);
  const unpaidLeaveHours = staffLeaves.filter(l => !l.IsPaid).reduce((total, leave) => total + (leave.TotalHours || 0), 0);
  const hasPaidLeaves = staffLeaves.some(l => l.IsPaid);
  const hasUnpaidLeaves = staffLeaves.some(l => !l.IsPaid);




  const applyEntryTypeFilter = (entries) => {
    if (filters.entryType === 'All') return entries;
    
    return entries.filter(entry => {
      if (filters.entryType === 'Regular' && !entry.isBreak && !entry.isOvertime) return true;
      if (filters.entryType === 'Break' && entry.isBreak) return true;
      if (filters.entryType === 'Overtime' && entry.isOvertime) return true;
      return false;
    });
  };




  const applyNotesFilter = (entries) => {
    if (!filters.searchNotes.trim()) return entries;
    
    const searchLower = filters.searchNotes.toLowerCase();
    return entries.filter(entry => 
      entry.notes?.toLowerCase().includes(searchLower)
    );
  };




  const applyPaidStatusFilter = (entries) => {
    if (filters.paidStatus === 'All') return entries;

    return entries.filter((entry) => {
      const isPaid = getEntryPaidStatus(entry);
      return filters.paidStatus === 'Paid' ? isPaid : !isPaid;
    });
  };




  const resolveNormalRate = (entry, dayRecord = {}) => {
    return toNumber(
      entry?.normalHourlyRate ??
      entry?.NormalHourlyRate ??
      entry?.ratePerHour ??
      entry?.RatePerHour ??
      dayRecord?.ratePerHour ??
      staffData?.ratePerHour ??
      staffData?.RatePerHour ??
      reportData?.weekStats?.ratePerHour
    );
  };

  const resolveOvertimeRate = (entry, dayRecord = {}) => {
    return toNumber(
      entry?.overtimeHourlyRate ??
      entry?.OvertimeHourlyRate ??
      dayRecord?.overtimeHourlyRate ??
      staffData?.overTimeHourlyRate ??
      staffData?.OverTimeHourlyRate ??
      reportData?.weekStats?.overTimeHourlyRate ??
      resolveNormalRate(entry, dayRecord)
    );
  };

  const calculateDayStats = (entries, dayRecord = {}) => {
    return entries.reduce((acc, entry) => {
      const workedHours =
        toNumber(entry?.workedHours ?? entry?.Worked_Hours ?? entry?.WorkedHours ?? entry?.worked_hours ?? 0);
      let entryPay = toNumber(entry?.entryPay ?? entry?.EntryPay ?? entry?.entry_pay ?? 0);
      const isBreak = Boolean(entry?.isBreak);
      const isOvertime = Boolean(entry?.isOvertime);
      const breakType = (entry?.breakType || '').toString().toUpperCase();
      const normalRateResolved = resolveNormalRate(entry, dayRecord);
      const overtimeRateResolved = resolveOvertimeRate(entry, dayRecord);

      if (entryPay === 0 && !isSalaryStaff) {
        if (isBreak) {
          if (breakType !== 'UNPAID' && breakType !== 'UN-PAID') {
            entryPay = normalRateResolved * workedHours;
          }
        } else {
          entryPay = (isOvertime ? overtimeRateResolved : normalRateResolved) * workedHours;
        }
      }

      if (isBreak) {
        // Breaks should NOT be included in totalHours
        // Only track break hours separately
        acc.totalBreakHours += workedHours;
        if (breakType === 'UNPAID' || breakType === 'UN-PAID') {
          acc.unpaidBreakHours += workedHours;
        }
        if (entryPay) {
          acc.totalPay += entryPay;
        }
      } else {
        // Only non-break entries contribute to totalHours
        acc.totalHours += workedHours;
        if (isOvertime) {
          acc.overtimeHours += workedHours;
        } else {
          acc.regularHours += workedHours;
        }
        acc.totalPay += entryPay;
      }

      return acc;
    }, {
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      totalHours: 0,
      totalPay: 0
    });
  };
  const filteredDailyRecords = useMemo(() => {
    if (!reportData?.dailyRecords) return [];

    return reportData.dailyRecords
      .map((dayRecord) => {
        const entries = applyNotesFilter(
          applyEntryTypeFilter(
            applyPaidStatusFilter(dayRecord.entries || [])
          )
        );

        if (!entries || entries.length === 0) {
          return null;
        }

        const dayStats = calculateDayStats(entries, dayRecord);

        const totalBreakHours = dayStats.totalBreakHours || dayRecord.totalBreakHours || 0;
        const unpaidBreakHours = dayStats.unpaidBreakHours || dayRecord.unpaidBreakHours || 0;
        const extraBreakHours = dayRecord.extraBreakHours || 0;
        const hasPaidBreaks = entries.some((entry) => {
          if (!entry.isBreak) return false;
          const breakType = (entry.breakType || '').toString().toUpperCase();
          return breakType !== 'UNPAID' && breakType !== 'UN-PAID';
        });
        const extraBreakToSubtract = hasPaidBreaks ? extraBreakHours : 0;
        const payableHours = Math.max(
          (dayStats.totalHours || 0) - unpaidBreakHours - extraBreakToSubtract,
          0
        );

        const regularHours = dayStats.regularHours || 0;
        const overtimeHours = dayStats.overtimeHours || 0;
        const workingHours = regularHours + overtimeHours;
        const normalRate =
          entries.find((entry) => !entry.isOvertime && !entry.isBreak && entry.normalHourlyRate > 0)?.normalHourlyRate ||
          dayRecord.ratePerHour ||
          0;
        const overtimeRate =
          entries.find((entry) => entry.isOvertime && entry.overtimeHourlyRate > 0)?.overtimeHourlyRate || 0;
        const avgHourlyRate = workingHours > 0 ? (dayStats.totalPay || 0) / workingHours : 0;
        const effectiveRate = normalRate || overtimeRate || avgHourlyRate || 0;
        const payableEarnings = !isSalaryStaff ? payableHours * effectiveRate : 0;

        return {
          ...dayRecord,
          ...dayStats,
          entries,
          entryCount: entries.length,
          computedPayableHours: payableHours,
          computedPayableEarnings: payableEarnings,
          computedEffectiveRate: effectiveRate
        };
      })
      .filter(Boolean);
  }, [reportData?.dailyRecords, filters.entryType, filters.paidStatus, filters.searchNotes]);




  const filteredWeekStats = useMemo(() => {
    if (!reportData?.weekStats && !filteredDailyRecords.length) {
      return null;
    }

    if (!filteredDailyRecords.length) {
      if (reportData?.weekStats) {
        return {
          daysWorked: reportData.weekStats.daysWorked || 0,
          regularHours: reportData.weekStats.regularHours || 0,
          overtimeHours: reportData.weekStats.overtimeHours || 0,
          unpaidBreakHours: reportData.weekStats.unpaidBreakHours || 0,
          totalBreakHours: reportData.weekStats.totalBreakHours || 0,
          extraBreakHours: reportData.weekStats.extraBreakHours || 0,
          totalHours: reportData.weekStats.totalHours || 0,
          totalEarnings: reportData.weekStats.totalEarnings || 0
        };
      }

      return {
        daysWorked: 0,
        regularHours: 0,
        overtimeHours: 0,
        unpaidBreakHours: 0,
        totalBreakHours: 0,
        extraBreakHours: 0,
        totalHours: 0,
        totalEarnings: 0
      };
    }

    const aggregate = filteredDailyRecords.reduce((acc, dayRecord) => {
      acc.daysWorked += 1;
      acc.regularHours += dayRecord.regularHours || 0;
      acc.overtimeHours += dayRecord.overtimeHours || 0;
      acc.unpaidBreakHours += dayRecord.unpaidBreakHours || 0;
      acc.totalBreakHours += dayRecord.totalBreakHours || 0;
      acc.extraBreakHours += dayRecord.extraBreakHours || 0;
      acc.totalHours += dayRecord.totalHours || 0;
      acc.totalEarnings += dayRecord.computedPayableEarnings || dayRecord.totalPay || 0;
      return acc;
    }, {
      daysWorked: 0,
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      extraBreakHours: 0,
      totalHours: 0,
      totalEarnings: 0
    });

    if (reportData?.weekStats) {
      return {
        ...reportData.weekStats,
        ...aggregate,
        daysWorked: aggregate.daysWorked,
        totalEarnings: isSalaryStaff
          ? (reportData.weekStats.totalEarnings ?? aggregate.totalEarnings)
          : aggregate.totalEarnings
      };
    }

    return aggregate;
  }, [reportData?.weekStats, filteredDailyRecords]);




  const weekStats = filteredWeekStats;




  if (!staffData) {
    return (<GridSectionLoader loading={true} minHeight="70vh" />);
  }

  return (
    <div className="staff-shift-report">
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/staff/manageStaff">Staff</a></li>
        <li className="breadcrumb-item active">Staff Shift Report</li>
      </ol>
      <h1 className="page-header">
        Staff Shift Report – {([staffData.FirstName ?? staffData.firstName, staffData.LastName ?? staffData.lastName].filter(Boolean).join(" ").trim()) || UserName} ({UserName})
      </h1>

      <div className="card border-0">
        <StaffTabBar
          activeTab="workReport"
          useTab="false"
          UserName={UserName}
          onBeforeTabChange={handleTabChangeBeforeNavigate}
        />

        {/* Filters — manage-page ModernGridToolbar layout */}
        <div className="pt-3 mb-4" style={{ position: 'relative', zIndex: 10 }}>
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Shift Days"
            totalCount={filteredDailyRecords.length}
            searchValue={filters.searchNotes}
            onSearchChange={(value) => handleFilterChange('searchNotes', value)}
            searchPlaceholder="Search notes..."
            topFilters={
              <>
                <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                  <DateRangePicker
                    startDate={startDate && moment(startDate).isValid() ? startDate : null}
                    endDate={endDate && moment(endDate).isValid() ? endDate : null}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill
                  iconClass="fa fa-list-check"
                  className="modern-grid-toolbar__filter-pill--status"
                >
                  <Select
                    value={
                      ENTRY_TYPE_OPTIONS.find((o) => o.value === filters.entryType) ||
                      ENTRY_TYPE_OPTIONS[0]
                    }
                    onChange={(option) =>
                      handleFilterChange('entryType', option?.value || 'All')
                    }
                    options={ENTRY_TYPE_OPTIONS}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
                <ModernGridToolbarFilterPill
                  iconClass="fa fa-money-bill"
                  className="modern-grid-toolbar__filter-pill--status"
                >
                  <Select
                    value={
                      PAID_STATUS_OPTIONS.find((o) => o.value === filters.paidStatus) ||
                      PAID_STATUS_OPTIONS[0]
                    }
                    onChange={(option) =>
                      handleFilterChange('paidStatus', option?.value || 'All')
                    }
                    options={PAID_STATUS_OPTIONS}
                    styles={filterPillSelectStyles}
                    isSearchable={false}
                    {...defaultSelectPortalProps}
                  />
                </ModernGridToolbarFilterPill>
              </>
            }
          />
        </div>

        <GridSectionLoader loading={loading} minHeight="280px">
        <>
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {weekStats && (
          <div className="mb-4" style={{ position: 'relative', zIndex: 0 }}>
            <div className="staff-shift-report__kpi-grid">
              <div className="staff-shift-report__kpi is-accent" style={{ backgroundColor: themeColor }}>
                <div className="staff-shift-report__kpi-icon"><i className="fa fa-clock"></i></div>
                <div className="staff-shift-report__kpi-body">
                  <div className="staff-shift-report__kpi-value">{weekStats.totalHours.toFixed(2)}</div>
                  <div className="staff-shift-report__kpi-label">Total Hours</div>
                </div>
              </div>

              <div className="staff-shift-report__kpi">
                <div className="staff-shift-report__kpi-icon" style={{ background: 'rgba(13,110,253,0.12)', color: '#0d6efd' }}>
                  <i className="fa fa-arrow-trend-up"></i>
                </div>
                <div className="staff-shift-report__kpi-body">
                  <div className="staff-shift-report__kpi-value">{weekStats.overtimeHours.toFixed(2)}</div>
                  <div className="staff-shift-report__kpi-label">Overtime</div>
                </div>
              </div>

              <div className="staff-shift-report__kpi">
                <div className="staff-shift-report__kpi-icon" style={{ background: 'rgba(255,193,7,0.15)', color: '#d39e00' }}>
                  <i className="fa fa-coffee"></i>
                </div>
                <div className="staff-shift-report__kpi-body">
                  <div className="staff-shift-report__kpi-value">{weekStats.totalBreakHours.toFixed(2)}</div>
                  <div className="staff-shift-report__kpi-label">Break Hours</div>
                </div>
              </div>

              {weekStats.extraBreakHours > 0 && (
                <div className="staff-shift-report__kpi is-warning">
                  <div className="staff-shift-report__kpi-icon"><i className="fa fa-exclamation-triangle"></i></div>
                  <div className="staff-shift-report__kpi-body">
                    <div className="staff-shift-report__kpi-value">{weekStats.extraBreakHours.toFixed(2)}</div>
                    <div className="staff-shift-report__kpi-label">Extra Break</div>
                  </div>
                </div>
              )}

              <div className="staff-shift-report__kpi is-accent" style={{ backgroundColor: themeColor }}>
                <div className="staff-shift-report__kpi-icon"><i className="fa fa-money-bill-wave"></i></div>
                <div className="staff-shift-report__kpi-body">
                  <div className="staff-shift-report__kpi-value">{formatCurrency(weekStats.totalEarnings)}</div>
                  <div className="staff-shift-report__kpi-label">Earnings</div>
                </div>
              </div>

              <div className="staff-shift-report__kpi">
                <div className="staff-shift-report__kpi-icon" style={{ background: 'rgba(25,135,84,0.12)', color: '#198754' }}>
                  <i className="fa fa-calendar-days"></i>
                </div>
                <div className="staff-shift-report__kpi-body">
                  <div className="staff-shift-report__kpi-value">{weekStats.daysWorked}</div>
                  <div className="staff-shift-report__kpi-label">Days Worked</div>
                </div>
              </div>

              <div className="staff-shift-report__kpi">
                <div className="staff-shift-report__kpi-icon" style={{ background: 'rgba(111,66,193,0.12)', color: '#6f42c1' }}>
                  <i className="fa fa-hand-holding-dollar"></i>
                </div>
                <div className="staff-shift-report__kpi-body">
                  {weekStats.isSalaryStaff || isSalaryStaff ? (
                    <>
                      <div className="staff-shift-report__kpi-value">{formatCurrency(weekStats.salary)}</div>
                      <div className="staff-shift-report__kpi-label">
                        {weekStats.salaryType === 'Monthly' ? 'Monthly Salary' : 'Weekly Salary'}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="staff-shift-report__kpi-value">{formatCurrency(weekStats.ratePerHour)}/hr</div>
                      <div className="staff-shift-report__kpi-label">Hourly Rate</div>
                    </>
                  )}
                </div>
              </div>

              {totalLeaveHours > 0 && (
                <div className="staff-shift-report__kpi is-muted-kpi">
                  <div className="staff-shift-report__kpi-icon"><i className="fa fa-calendar-times"></i></div>
                  <div className="staff-shift-report__kpi-body">
                    <div className="staff-shift-report__kpi-value">{totalLeaveHours.toFixed(1)}</div>
                    <div className="staff-shift-report__kpi-label">Leave Hours</div>
                    {paidLeaveHours > 0 && unpaidLeaveHours > 0 && (
                      <div className="staff-shift-report__kpi-sub">
                        {paidLeaveHours.toFixed(1)} paid · {unpaidLeaveHours.toFixed(1)} unpaid
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {staffLeaves.length > 0 && (
          <div className="staff-shift-report__panel mb-4">
            <div className="staff-shift-report__panel-head">
              <h3 className="staff-shift-report__panel-title">
                <i className="fa fa-calendar-times"></i>
                Leave Records
              </h3>
              <span className="badge bg-secondary">
                {staffLeaves.length} leave{staffLeaves.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="staff-shift-report__leave-grid">
              {staffLeaves.map((leave) => (
                <div key={leave.SL_ID} className="staff-shift-report__leave-card">
                  <div className="staff-shift-report__leave-top">
                    <h6 className="staff-shift-report__leave-title">{leave.LeaveType || 'Leave'}</h6>
                    <span className={`badge ${leave.IsPaid ? 'bg-success' : 'bg-secondary'}`}>
                      {leave.IsPaid ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                  <div className="small text-muted">
                    {moment(leave.StartDate).format('MMM DD, YYYY')} – {moment(leave.EndDate).format('MMM DD, YYYY')}
                  </div>
                  <div className="small text-muted mt-1">{(leave.TotalHours || 0).toFixed(1)} hours</div>
                  {leave.Reason && (
                    <div className="staff-shift-report__entry-notes mt-2">"{leave.Reason}"</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredDailyRecords && filteredDailyRecords.length > 0 ? (
          <div className="staff-shift-report__panel mb-3">
            <div className="staff-shift-report__panel-head">
              <h3 className="staff-shift-report__panel-title">
                <i className="fa fa-calendar-days"></i>
                Daily Time Records
              </h3>
              <span className="badge bg-primary">{filteredDailyRecords.length} days</span>
            </div>

            <div className="staff-shift-report__day-head" aria-hidden="true">
              <span>Date</span>
              <span className="is-end">Total</span>
              <span className="is-end">Payable</span>
              <span className="is-end">Rate</span>
              <span className="is-end">Earnings</span>
            </div>

            {filteredDailyRecords.map((dayRecord) => {
              const isExpanded = expandedDays.has(dayRecord.date);
              const totalBreakHours = dayRecord.totalBreakHours || dayRecord.unpaidBreakHours || 0;
              const extraBreakHours = dayRecord.extraBreakHours || 0;
              const allowedBreakHours = dayRecord.allowedBreakHours || 0;
              const payableHours = dayRecord.computedPayableHours ?? dayRecord.totalHours ?? 0;
              const regularHours = dayRecord.regularHours || 0;
              const overtimeHours = dayRecord.overtimeHours || 0;
              const workingHours = regularHours + overtimeHours;
              const normalRate = dayRecord.entries.find(e => !e.isOvertime && e.normalHourlyRate > 0)?.normalHourlyRate || 0;
              const overtimeRate = dayRecord.entries.find(e => e.isOvertime && e.overtimeHourlyRate > 0)?.overtimeHourlyRate || 0;
              const avgHourlyRate = dayRecord.computedEffectiveRate ?? (workingHours > 0 ? dayRecord.totalPay / workingHours : 0);

              return (
                <div key={dayRecord.date} className="staff-shift-report__day">
                  <div
                    className={`staff-shift-report__day-row${isExpanded ? ' is-expanded' : ''}`}
                    onClick={() => toggleDayExpansion(dayRecord.date)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleDayExpansion(dayRecord.date);
                      }
                    }}
                    aria-expanded={isExpanded}
                  >
                    <div className="staff-shift-report__day-chevron">
                      <i className="fa fa-chevron-right"></i>
                    </div>

                    <div className="staff-shift-report__day-meta">
                      <h6 className="staff-shift-report__day-date">{formatDate(dayRecord.date)}</h6>
                      <div className="staff-shift-report__day-badges">
                        <span className="badge bg-secondary">
                          {dayRecord.entries.length} {dayRecord.entries.length === 1 ? 'entry' : 'entries'}
                        </span>
                        {overtimeHours > 0 && (
                          <span className="badge bg-info text-dark">OT {overtimeHours.toFixed(2)}h</span>
                        )}
                        {totalBreakHours > 0 && (
                          <span className="badge bg-warning text-dark">Break {totalBreakHours.toFixed(2)}h</span>
                        )}
                        {extraBreakHours > 0 && (
                          <span className="badge bg-danger" title={`Allowed: ${allowedBreakHours.toFixed(1)}h`}>
                            Extra +{extraBreakHours.toFixed(2)}h
                          </span>
                        )}
                        {dayRecord.totalHours > 24 && (
                          <span className="badge bg-dark">
                            <i className="fa fa-moon me-1"></i>
                            Overnight
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="staff-shift-report__day-metrics">
                      <div className="staff-shift-report__metric">
                        <span className="staff-shift-report__metric-label">Total</span>
                        <div className="staff-shift-report__metric-value is-success">
                          {dayRecord.totalHours.toFixed(2)} hrs
                        </div>
                      </div>
                      <div className="staff-shift-report__metric">
                        <span className="staff-shift-report__metric-label">Payable</span>
                        <div className="staff-shift-report__metric-value is-primary">
                          {payableHours.toFixed(2)} hrs
                        </div>
                      </div>
                      <div className="staff-shift-report__metric">
                        <span className="staff-shift-report__metric-label">
                          {dayRecord.overtimeHours > 0 ? 'Avg Rate' : 'Rate'}
                        </span>
                        <div className="staff-shift-report__metric-value">
                          {(normalRate > 0) && `${formatCurrency(normalRate)}/hr`}
                          {(overtimeRate > 0) && (
                            <span className="staff-shift-report__metric-sub">
                              {formatCurrency(overtimeRate)}/hr OT
                            </span>
                          )}
                          {(!normalRate && !overtimeRate && avgHourlyRate > 0) && (
                            `${formatCurrency(avgHourlyRate)}/hr`
                          )}
                          {(!normalRate && !overtimeRate && !(avgHourlyRate > 0)) && '—'}
                        </div>
                      </div>
                      <div className="staff-shift-report__metric">
                        <span className="staff-shift-report__metric-label">Earnings</span>
                        <div className="staff-shift-report__metric-value is-primary">
                          {formatCurrency(dayRecord.computedPayableEarnings ?? dayRecord.totalPay)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="staff-shift-report__entries">
                      {dayRecord.entries.map((entry) => {
                        const isEntryPaid = getEntryPaidStatus(entry);
                        return (
                          <div key={entry.timeTracking_ID} className="staff-shift-report__entry">
                            <div className="staff-shift-report__entry-icon">
                              <i className={getEntryIcon(entry)}></i>
                            </div>
                            <div className="staff-shift-report__entry-body">
                              <div className="staff-shift-report__entry-top">
                                <h6 className="staff-shift-report__entry-time">
                                  {entry.startTime} – {entry.endTime || 'Active'}
                                </h6>
                                <span className={`badge ${getEntryBadgeClass(entry)}`}>
                                  {getEntryTypeLabel(entry)}
                                </span>
                                <span className={`badge ${isEntryPaid ? 'bg-success' : 'bg-danger'}`}>
                                  {isEntryPaid ? 'Paid' : 'Unpaid'}
                                </span>
                                {entry.isLate && <span className="badge bg-danger">Late</span>}
                                {entry.isEarly && <span className="badge bg-info text-dark">Early</span>}
                                {entry.isManualEntry && <span className="badge bg-secondary">Manual</span>}
                                {entry.isOvernightShift && <span className="badge bg-dark">Overnight</span>}
                                {entry.isActiveShift && <span className="badge bg-success">Active</span>}
                              </div>
                              <div className="staff-shift-report__entry-meta">
                                <span>{entry.workedHours.toFixed(2)} hours</span>
                                {(entry.isOvertime && entry.overtimeHourlyRate > 0) ? (
                                  <span>{formatCurrency(entry.overtimeHourlyRate)}/hr (OT)</span>
                                ) : (entry.normalHourlyRate > 0) && (
                                  <span>{formatCurrency(entry.normalHourlyRate)}/hr</span>
                                )}
                                {entry.entryPay > 0 && (
                                  <span className="fw-semibold text-success">{formatCurrency(entry.entryPay)}</span>
                                )}
                                {entry.deviceName && (
                                  <span><i className="fa fa-mobile me-1"></i>{entry.deviceName}</span>
                                )}
                              </div>
                              {entry.notes && (
                                <div className="staff-shift-report__entry-notes">"{entry.notes}"</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : !loading && reportData && (
          <div className="staff-shift-report__panel">
            <div className="staff-shift-report__empty">
              <i className="fa fa-calendar-xmark fa-3x text-muted mb-3"></i>
              <h5>No Shift Records Found</h5>
              <p className="text-muted mb-3">
                No shift records found for {staffData.FirstName} {staffData.LastName}
                in the selected date range.
              </p>
              <button className="btn btn-primary" onClick={resetFilters}>
                Reset Filters
              </button>
            </div>
          </div>
        )}
        </>
        </GridSectionLoader>
      </div>
    </div>
  );
};

export default StaffShiftReport;